// Core HTTP client. Wires DPoP proofs, DPoP-Nonce retry, bearer auth, 401→renew,
// idempotency keys, and the shared error envelope — per API-CONTRACT §2–§4 + ADR-002.

import { config } from '../config.js';
import { encConfig, dualWriteActive } from '../config-enc.js';
import { dpopProof, resetDPoPKeypair } from '../crypto/dpop.js';
import { session } from './session.js';
import type { ApiErrorBody, AuthSuccess } from './types.js';
import { getMock } from './mock.js';
import {
  buildDualWriteErrorRecord,
  recordDualWriteError,
  installDualWriteErrorConsoleHelpers,
  type DualWriteErrorRecord,
} from '../lib/dual-write-log.js';

// Timeout (ms) for a single encrypted-follower mirror attempt. A hung api2 must
// never block the UI indefinitely; a timeout counts as a (logged) mirror failure.
const MIRROR_TIMEOUT_MS = 5000;

// Install the browser console review helpers for the dual-write error buffer
// (window.__vidDualWriteErrors / __vidDualWriteErrorsClear). Only meaningful in
// the app2 build; harmless (no-op sink) otherwise, but we still gate on
// dualWriteActive() so the default build never touches window.
if (dualWriteActive()) installDualWriteErrorConsoleHelpers();

// Encrypted-stack dual-write (app2 build only). DEFAULT-OFF: dualWriteActive() is
// a runtime read of the `__VID_DUAL_WRITE__` / `__VID_ENC_API_BASE__` esbuild
// defines. In the normal (default) build those defines resolve to 'false'/'' so
// dualWriteActive() is constant-false; esbuild's minifier constant-folds the
// guarded branch away, leaving the single-write path byte-for-byte as today and
// never emitting the api2 base string into the bundle.
const MUTATING = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

// Build the same request path/query against the encrypted follower base (api2).
function buildEncUrl(path: string, query?: RequestOpts['query']): string {
  const base = (encConfig.encApiBase ?? '').replace(/\/$/, '');
  const url = new URL(base + path);
  if (query) for (const [k, v] of Object.entries(query)) if (v !== undefined) url.searchParams.set(k, String(v));
  return url.toString();
}

// Best-effort authed POST of a dual-write error record to the AUTHORITATIVE api's
// central log endpoint. Fire-and-forget from the caller's perspective — a failed
// report simply leaves the record in the client-side localStorage buffer. Reuses
// the normal request path so it carries bearer + a fresh DPoP proof.
async function reportDualWriteError(rec: DualWriteErrorRecord): Promise<void> {
  // auth:true (default) attaches bearer + DPoP; raw:false; no body echoing of the
  // failed mutation — only the metadata record. This goes to api.virtualid.one.
  await doFetch<void>('/dual-write-errors', { method: 'POST', body: rec });
}

// Blocking-but-NON-FATAL mirror of a SUCCESSFUL authoritative mutation to the
// encrypted follower API. The caller AWAITS this before returning, but a failure
// here (throw / non-2xx / timeout) is SWALLOWED — it is logged to the separate
// error log (server file + localStorage buffer) and NEVER rejects the caller's
// promise, so the user's committed action always succeeds (authoritative first,
// mirror second, reconcile later). A FRESH DPoP proof is minted for the api2 htu
// because DPoP htu is URL-specific and cannot be reused across origins; the
// bearer token + idempotency key are carried over unchanged. /auth/* is
// intentionally EXCLUDED — the encrypted stack performs its own first-login key
// rotation server-side, so mirroring auth ceremonies would be wrong/harmful.
async function mirrorToEncrypted(path: string, opts: RequestOpts, method: string): Promise<void> {
  if (path.startsWith('/auth/')) return; // exclude auth ceremonies (see above)
  const url = buildEncUrl(path, opts.query);
  let status = 0;
  try {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    const useAuth = opts.auth !== false;
    if (useAuth && session.access) headers['Authorization'] = `Bearer ${session.access}`;
    // Fresh DPoP proof bound to the api2 htu (htu is URL-specific).
    if (useAuth || opts.dpop || session.access) {
      headers['DPoP'] = await dpopProof(method, url, {
        nonce: session.nonce ?? undefined,
        accessToken: session.access ?? undefined,
      });
    }
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    if (opts.ifMatch) headers['If-Match'] = opts.ifMatch;
    if (opts.stepUp) headers['VID-Step-Up'] = opts.stepUp;
    // Bound the mirror so a hung api2 can't block the UI: timeout => abort =>
    // counts as a (logged, non-fatal) failure.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), MIRROR_TIMEOUT_MS);
    try {
      const res = await fetch(url, {
        method,
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        credentials: 'omit',
        signal: ctrl.signal,
      });
      status = res.status;
      if (!res.ok) throw new Error(`enc mirror ${method} ${path} -> HTTP ${res.status}`);
    } finally {
      clearTimeout(timer);
    }
  } catch (err) {
    // Drift is logged to the separate error log, never thrown: the authoritative
    // write is already durable. Best-effort server report + durable client buffer.
    const rec = buildDualWriteErrorRecord({
      method,
      path,
      api2Url: url,
      status,
      error: err,
      idempotencyKey: opts.idempotencyKey,
    });
    // Do NOT report the report endpoint's own path back to itself.
    recordDualWriteError(rec, path === '/dual-write-errors' ? undefined : reportDualWriteError);
  }
}

export class ApiError extends Error {
  code: string; status: number; requestId?: string; field?: string;
  constructor(status: number, body?: ApiErrorBody) {
    super(body?.error?.message || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code || `http_${status}`;
    this.requestId = body?.error?.request_id;
    this.field = body?.error?.field;
  }
}

export interface RequestOpts {
  method?: string;
  body?: unknown;
  auth?: boolean;          // attach bearer + DPoP (default true)
  dpop?: boolean;          // force-attach a DPoP proof even when auth:false (register/login finish bind the session to the DPoP key)
  idempotencyKey?: string; // for creating POSTs
  ifMatch?: string;        // optimistic concurrency
  stepUp?: string;         // VID-Step-Up assertion
  query?: Record<string, string | number | undefined>;
  raw?: boolean;           // return Response (for downloads)
  _renewed?: boolean;      // internal: prevent infinite renew loop
  _nonceRetried?: boolean; // internal: single nonce retry
}

function buildUrl(path: string, query?: RequestOpts['query']): string {
  const url = new URL(config.apiBase.replace(/\/$/, '') + path);
  if (query) for (const [k, v] of Object.entries(query)) if (v !== undefined) url.searchParams.set(k, String(v));
  return url.toString();
}

async function doFetch<T>(path: string, opts: RequestOpts): Promise<T> {
  const method = (opts.method || 'GET').toUpperCase();
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  const useAuth = opts.auth !== false;
  if (useAuth && session.access) headers['Authorization'] = `Bearer ${session.access}`;

  // DPoP proof: bound to htm+htu, with ath when we have an access token.
  // register/login finish set dpop:true so the session is bound to a DPoP key
  // even though they carry no bearer token (server requires the proof there).
  if (useAuth || opts.dpop || session.access) {
    headers['DPoP'] = await dpopProof(method, url, {
      nonce: session.nonce ?? undefined,
      accessToken: session.access ?? undefined,
    });
  }
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  if (opts.ifMatch) headers['If-Match'] = opts.ifMatch;
  if (opts.stepUp) headers['VID-Step-Up'] = opts.stepUp;

  const res = await fetch(url, {
    method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: 'omit',
  });

  // Capture server-issued DPoP nonce and retry once (RFC 9449 use_dpop_nonce dance).
  const nonce = res.headers.get('DPoP-Nonce');
  if (nonce) session.setNonce(nonce);
  if (res.status === 401 && nonce && !opts._nonceRetried) {
    const err = await safeJson<ApiErrorBody>(res);
    if (err?.error?.code === 'use_dpop_nonce') {
      return doFetch<T>(path, { ...opts, _nonceRetried: true });
    }
    // fall through to normal 401 handling with the parsed body
    return handle401<T>(path, opts, res.status, err);
  }

  if (res.status === 401 && useAuth) {
    return handle401<T>(path, opts, res.status, await safeJson<ApiErrorBody>(res));
  }

  if (opts.raw) {
    if (!res.ok) throw new ApiError(res.status, await safeJson<ApiErrorBody>(res) ?? undefined);
    if (dualWriteActive() && MUTATING.has(method)) {
      try { await mirrorToEncrypted(path, opts, method); } catch { /* already logged inside; never rethrow */ }
    }
    return res as unknown as T;
  }

  if (res.status === 204) {
    if (dualWriteActive() && MUTATING.has(method)) {
      try { await mirrorToEncrypted(path, opts, method); } catch { /* already logged inside; never rethrow */ }
    }
    return undefined as T;
  }

  const json = await safeJson<any>(res);
  if (!res.ok) throw new ApiError(res.status, json ?? undefined);
  // Authoritative mutation committed OK -> blocking-but-non-fatal mirror to the
  // encrypted follower (awaited; a failure is logged, never rethrown).
  if (dualWriteActive() && MUTATING.has(method)) {
    try { await mirrorToEncrypted(path, opts, method); } catch { /* already logged inside; never rethrow */ }
  }
  return json as T;
}

async function handle401<T>(path: string, opts: RequestOpts, status: number, body: ApiErrorBody | null): Promise<T> {
  if (!opts._renewed && session.refresh) {
    const ok = await tryRenew();
    if (ok) return doFetch<T>(path, { ...opts, _renewed: true });
  }
  // Unrecoverable 401: the session is gone/expired and renewal failed (or there was
  // nothing to renew). Tear down local auth and bounce to /login WITHOUT waiting for a
  // manual page refresh, so any click that triggers an authed call redirects immediately.
  onSessionExpired();
  throw new ApiError(status, body ?? undefined);
}

// redirectToLogin is an optional hook the app installs so the HTTP layer can navigate on an
// unrecoverable 401 without importing the router (avoids a cycle). Defaults to a hash bounce.
let redirectToLogin: (() => void) | null = null;
export function setSessionExpiredHandler(fn: () => void): void { redirectToLogin = fn; }

function onSessionExpired(): void {
  session.clear();
  resetDPoPKeypair();
  // Never bounce if we're already on a public/login route (avoids a redirect loop).
  if (typeof location !== 'undefined') {
    const hash = location.hash.replace(/^#/, '') || '/';
    const [p] = hash.split('?');
    const isPublic = p === '/login' || p === '/join' || p.startsWith('/public/') || p.startsWith('/subscribe') || p.startsWith('/id/');
    if (isPublic) return;
  }
  if (redirectToLogin) { redirectToLogin(); return; }
  if (typeof location !== 'undefined') location.hash = '#/login';
}

async function safeJson<T>(res: Response): Promise<T | null> {
  try { return (await res.json()) as T; } catch { return null; }
}

// --- Token renewal (ADR-002): present refresh + DPoP proof for the (possibly new) key. ---
let renewInFlight: Promise<boolean> | null = null;
async function tryRenew(): Promise<boolean> {
  if (renewInFlight) return renewInFlight;
  renewInFlight = (async () => {
    try {
      const url = buildUrl('/auth/token/renew');
      const headers: Record<string, string> = {
        Accept: 'application/json', 'Content-Type': 'application/json',
        DPoP: await dpopProof('POST', url, { nonce: session.nonce ?? undefined }),
      };
      const res = await fetch(url, {
        method: 'POST', headers,
        body: JSON.stringify({ refresh_token: session.refresh }),
      });
      const n = res.headers.get('DPoP-Nonce'); if (n) session.setNonce(n);
      if (!res.ok) return false;
      const tokens = (await res.json()) as AuthSuccess;
      session.set(tokens);
      return true;
    } catch { return false; }
  })();
  const out = await renewInFlight;
  renewInFlight = null;
  return out;
}

// Public request entry point — routes to the in-memory mock when config.useMock.
export async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  if (config.useMock) {
    try {
      return await getMock().handle<T>(path, opts);
    } catch (e: unknown) {
      const api = (e as { __api?: { status: number; body?: ApiErrorBody } }).__api;
      if (api) throw new ApiError(api.status, api.body);
      throw e;
    }
  }
  return doFetch<T>(path, opts);
}

export function logoutLocal(): void {
  session.clear();
  resetDPoPKeypair();
}

// On a fresh page load the in-memory access token is gone. If we have a
// persisted "had session" hint + refresh token, attempt a silent renew BEFORE
// the first authed request so the user lands authenticated. ensureDPoPKeypair
// (via tryRenew's dpopProof) reloads the persisted, server-bound DPoP key, so
// the renew proof matches the pinned jkt and succeeds. Returns true if a live
// session was (re)established. Defensive: never throws.
export async function bootstrapSession(): Promise<boolean> {
  if (config.useMock) return session.isLoggedIn || session.hadSession;
  if (session.isLoggedIn) return true;
  if (!session.hadSession || !session.refresh) return false;
  try {
    const ok = await tryRenew();
    if (!ok) session.clear();
    return ok;
  } catch {
    return false;
  }
}
